import { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import Footer from "../Home/Footer";
import CourseHeader from "../Home/CourseHeader";
import FemaleLogo from "./FemaleLogo.jpg";
import MaleLogo from "./MaleLogo.jpg";
import CompanyHeader from "../Company/CompanyHeader";
import { FiArrowLeft } from "react-icons/fi";
import AxiosInstance from "../Home/AxiosInstance";
import "./Profiles.css";
import "../Home/publicPages.css";
import "../Company/employerPortal.css";
import "bootstrap/dist/css/bootstrap.min.css";

export default function ProfileDetails() {
  const { profileId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [internships, setInternships] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [headerType, setHeaderType] = useState("default");

  useEffect(() => {
    const previousPath = location.state?.from;
    if (previousPath?.includes("user-profiles")) {
      setHeaderType("company");
    } else if (previousPath?.includes("/courses")) {
      setHeaderType("courses");
    } else {
      setHeaderType("default");
    }
  }, [location.state]);

  const renderHeader = () => {
    switch (headerType) {
      case "company":
        return <CompanyHeader />;
      case "courses":
        return <CourseHeader />;
      default:
        return <CourseHeader />;
    }
  };

  useEffect(() => {
    const fetchProfileDetails = async () => {
      try {
        const response = await AxiosInstance.get(
          `companies/profile/${profileId}/`
        );
        setProfile(response.data);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch profile details.");
        setLoading(false);
      }
    };
    fetchProfileDetails();
  }, [profileId]);

  useEffect(() => {
    if (profileId) {
      const fetchCourseMaterials = async () => {
        try {
          const res = await AxiosInstance.get(
            `companies/user/course/${profileId}/`
          );
          setCourseMaterials(res.data);
        } catch (err) {
          console.error("Error fetching course materials:", err);
        }
      };
      fetchCourseMaterials();
    }
  }, [profileId]);

  useEffect(() => {
    const fetchInternships = async () => {
      try {
        const role = profile?.role?.role?.toLowerCase();
        const userId = profile?.user?.user_id;

        if (role === "hr" && userId) {
          const res = await AxiosInstance.get(
            `companies/internships/${userId}/`
          );
          setInternships(res.data);
        }
      } catch (err) {
        console.error("Error fetching internships:", err);
        setInternships([]);
      }
    };

    if (profile) {
      fetchInternships();
    }
  }, [profile]);

  useEffect(() => {
    const fetchCertifications = async () => {
      try {
        let endpoint = "";

        if (profile?.user) {
          endpoint = `user/certifications/${profile.user.user_id}/`;
        } else if (profile?.trainer) {
          endpoint = `trainer/certifications/${profile.trainer.trainer_id}/`;
        } else {
          return;
        }

        const response = await AxiosInstance.get(endpoint);
        setCertifications(response.data);
      } catch (error) {
        console.error("Error fetching certifications:", error);
        setCertifications([]);
      }
    };

    if (profile) {
      fetchCertifications();
    }
  }, [profile]);

  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        let endpoint = "";

        if (profile?.user) {
          endpoint = `user/achievements/${profile.user.user_id}/`;
        } else if (profile?.trainer) {
          endpoint = `trainer/achievements/${profile.trainer.trainer_id}/`;
        } else {
          return;
        }

        const response = await AxiosInstance.get(endpoint);
        setAchievements(response.data);
      } catch (error) {
        console.error("Error fetching achievements:", error);
        setAchievements([]);
      }
    };

    if (profile) {
      fetchAchievements();
    }
  }, [profile]);

  if (loading) return <p className="text-center mt-4">Loading...</p>;
  if (error) return <p className="text-danger text-center mt-4">{error}</p>;

  const isHR = profile?.role?.role?.toLowerCase() === "hr";
  const userOrTrainer = profile?.user || profile?.trainer;
  const fromPath = location.state?.from || "/courses";
  const isFromCompany = String(fromPath).includes("user-profiles");
  const displayName =
    userOrTrainer?.alias_name?.trim() ||
    `${userOrTrainer?.first_name || ""} ${userOrTrainer?.last_name || ""}`.trim() ||
    "Profile";

  const handleBack = () => {
    if (fromPath && fromPath !== "/courses") {
      navigate(fromPath);
    } else {
      navigate("/courses");
    }
  };

  return (
    <div
      className={
        isFromCompany
          ? "CompanyPage als-employer-page"
          : "CompanyPage1"
      }
    >
      {renderHeader()}
      <div className={isFromCompany ? "company-container py-4" : "container1"}>
        <div className="content py-4">
          <div className="navigation-button-container mb-3">
            {isFromCompany ? (
              <button
                type="button"
                className="navigate-button btn btn-outline-primary d-flex align-items-center"
                onClick={handleBack}
                style={{ gap: "6px" }}
              >
                <FiArrowLeft /> Back
              </button>
            ) : (
              <nav
                className="als-breadcrumb als-breadcrumb-light"
                aria-label="Breadcrumb"
              >
                <Link to="/">Home</Link>
                <span className="als-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <Link to="/courses">Courses</Link>
                <span className="als-breadcrumb-sep" aria-hidden="true">
                  ›
                </span>
                <span aria-current="page">{displayName}</span>
              </nav>
            )}
          </div>

          {profile && (
            <div className="d-flex flex-row justify-content-between align-items-start mb-3">
              <div className="flex-grow-1 me-3">
                <div className="profile-details">
                  <h1>{displayName}</h1>

                  <div className="mt-4">
                    <h5>About:</h5>
                    {(() => {
                      const aboutText = userOrTrainer?.about;

                      if (!aboutText?.trim()) {
                        return <p>No description provided.</p>;
                      }

                      const lines = aboutText
                        .split("\n")
                        .filter((line) => line.trim() !== "");
                      const hasBullets = lines.some(
                        (line) =>
                          line.trim().startsWith("- ") ||
                          line.trim().startsWith("* ")
                      );

                      if (hasBullets) {
                        return (
                          <ul>
                            {lines.map((line, index) => (
                              <li key={index}>
                                {line.trim().replace(/^[-*]\s*/, "")}
                              </li>
                            ))}
                          </ul>
                        );
                      } else {
                        return lines.map((line, index) => (
                          <p key={index}>{line}</p>
                        ));
                      }
                    })()}

                    <h5>Education:</h5>
                    {profile.user_education &&
                    profile.user_education.education ? (
                      <p>
                        {profile.user_education.education?.qualification ||
                          "Qualification not provided"}{" "}
                        (
                        {profile.user_education.specialization ||
                          "Specialization not provided"}
                        ) -{" "}
                        {profile.user_education.college_name ||
                          "College not provided"}
                      </p>
                    ) : (
                      <p>Education details not provided.</p>
                    )}

                    <h5>Work Experience:</h5>
                    {profile.experience && profile.experience.company_name ? (
                      <p>
                        {profile.experience.company_name} (
                        {profile.experience.role?.role || "Role not provided"})
                        -{" "}
                        {profile.experience.experience ||
                          "Duration not provided"}
                      </p>
                    ) : (
                      <p>Experience details not provided.</p>
                    )}

                    <h5>{isHR ? "Internships" : "Courses"}:</h5>
                    {isHR ? (
                      internships.length === 0 ? (
                        <p>No internships found.</p>
                      ) : (
                        internships.map((internship) => (
                          <ul key={internship.internship_id}>
                            <li>{internship.role}</li>
                          </ul>
                        ))
                      )
                    ) : courseMaterials.length === 0 ? (
                      <p>No course materials found.</p>
                    ) : (
                      courseMaterials.map((courseMat) => (
                        <div key={courseMat.course_material_id}>
                          <p>
                            {courseMat.course?.course_name ||
                              "Course name not available"}
                          </p>
                        </div>
                      ))
                    )}

                    <h5>Certifications:</h5>
                    {certifications.length === 0 ? (
                      <p>No certifications found.</p>
                    ) : (
                      <ul>
                        {certifications.map((cert) => (
                          <li
                            key={
                              cert.user_certification_id ||
                              cert.trainer_certification_id
                            }
                          >
                            {cert.certification_name} - {cert.issue_authority}
                          </li>
                        ))}
                      </ul>
                    )}

                    <h5>Achievements:</h5>
                    {achievements.length === 0 ? (
                      <p>No achievements found.</p>
                    ) : (
                      <ul>
                        {achievements.map((ach) => (
                          <li key={ach.achievement_id}>
                            {ach.title} - {ach.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Profile Image Section */}
              {userOrTrainer && (
                <div
                  className="profile-image-container mb-3"
                  style={{ minWidth: "180px", textAlign: "start" }}
                >
                  <img
                    src={
                      userOrTrainer.image_path
                        ? `${AxiosInstance.defaults.baseURL}/media/${userOrTrainer.image_path.replace(
                            /\\/g,
                            "/"
                          )}`
                        : userOrTrainer.gender?.toLowerCase() === "male"
                        ? MaleLogo
                        : FemaleLogo
                    }
                    alt="Profile"
                    className="profile-image"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
